'use client';
import { I18nextProvider } from 'react-i18next';
import { LocaleContext } from './LocaleContext';
import { type PropsWithChildren, useEffect, useState } from 'react';
import type { Locale } from '@utils/models';
import { useSelector } from 'react-redux';
import { detectRegionFromContext } from '../../app/localization/duck';
import { getDefaultLocaleByRegion } from '../../app/localization/state';
import { getOptions } from '../../app/localization/settings/settings';
import i18n from '../../app/localization/i18n';

export const InternationalizationClientProvider = ({ children }: PropsWithChildren) => {
  const reduxLocale = useSelector((state: { appContext?: { locale?: Locale } }) => state.appContext?.locale);
  const detectedRegion = detectRegionFromContext();
  const defaultLocale = getDefaultLocaleByRegion(detectedRegion);
  const [locale, setLocale] = useState<Locale>(reduxLocale || defaultLocale);

  useEffect(() => {
    if (reduxLocale && reduxLocale !== locale) {
      setLocale(reduxLocale);
    }
  }, [reduxLocale]);

  useEffect(() => {
    const resources = getOptions().resources;
    const availableNamespaces = Object.keys(resources[locale] || {});

    for (const ns of availableNamespaces) {
      const messages = resources[locale]?.[ns];
      if (messages && typeof messages === 'object' && messages !== null) {
        i18n.addResourceBundle(locale, ns, messages, true, true);
      }
    }

    i18n.changeLanguage(locale);
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LocaleContext.Provider>
  );
};
